import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Botao,
  Breadcrumb,
  Cabecalho,
  Filtro,
  InputUnform,
  TabelaRef,
  TabelaResposta,
  Tooltip,
  IconeEditar,
  IconeLixeira,
  IconeBloqueado,
  IconeAtencao,
  SelectAsyncUnform,
  SelectUnform,
  SelectOpcao,
  Tabela,
  ContainerAcoes,
  TabelaDados,
  FuncoesDataHora,
  Modal,
  ModalRef
} from 'src/componentes'
import { RotasPrecificacao } from 'src/rotas/academico'
import { Api } from 'src/servicos'
import { RequisitarListaCursosPorTextoFiltro } from 'src/servicos/api'
import { TipoOrdenacao } from 'src/tipos'
import {
  PrecificacaoCategoria,
  PrecificacaoCategoriaPorExtenso
} from 'src/tipos/precificacao'

import {
  CalloutAlertaModal,
  Campo,
  Container,
  Dados,
  Form,
  TextoConfirmacaoModal
} from './styles'

type AcaoConfirmacao = 'inativar' | 'excluir'

interface DadosPesquisa {
  nome: string
  codigo: string
  categoria: string
  ativo: string
  curso: string
}

export const PaginaPrecificacao: React.FC = () => {
  const tabelaRef = useRef<TabelaRef>()
  const modalConfirmacaoRef = useRef<ModalRef>(null)
  const [dadosPesquisa, setDadosPesquisa] = useState<DadosPesquisa | null>(null)
  const history = useHistory()
  const [paginas, setPaginas] = useState<number | null>(null)
  const [cursoSelecionado, definirCursoSelecionado] = useState<string>(null)
  const [acaoPendente, setAcaoPendente] = useState<{
    tipo: AcaoConfirmacao
    id: string
  } | null>(null)
  const [carregandoAcaoPendente, setCarregandoAcaoPendente] = useState(false)
  const [qtdCursosAfetados, definirQtdCursosAfetados] = useState<number | null>(
    null
  )

  const categoriasPrecificacao = useMemo(() => {
    return [
      {
        id: PrecificacaoCategoria.Base,
        texto: PrecificacaoCategoriaPorExtenso.Base
      },
      {
        id: PrecificacaoCategoria.Cpf,
        texto: PrecificacaoCategoriaPorExtenso.Cpf
      },
      {
        id: PrecificacaoCategoria.CampanhaPos,
        texto: PrecificacaoCategoriaPorExtenso.CampanhaPos
      },
      {
        id: PrecificacaoCategoria.CampanhaExtensao,
        texto: PrecificacaoCategoriaPorExtenso.CampanhaExtensao
      }
    ] as SelectOpcao[]
  }, [])

  const opcoesStatus = useMemo(() => {
    return [
      { id: 'true', texto: 'Ativa' },
      { id: 'false', texto: 'Inativa' }
    ] as SelectOpcao[]
  }, [])

  useEffect(() => {
    if (dadosPesquisa === null) return
    tabelaRef.current?.IrParaPagina()
    tabelaRef.current?.CarregarDados()
  }, [dadosPesquisa])

  const acaoPesquisa = (dados: DadosPesquisa) => {
    if (dados !== null && paginas !== 1) {
      setPaginas(1)
    }
    setDadosPesquisa(dados)
  }

  const cursoAlterado = (opcao: SelectOpcao) => {
    definirCursoSelecionado(opcao?.id)
  }

  const abrirConfirmacao = async (tipo: AcaoConfirmacao, id: string) => {
    setAcaoPendente({ tipo, id })
    definirQtdCursosAfetados(null)
    modalConfirmacaoRef.current?.abrir()

    try {
      const dados = await Api.ObterDadosPrecificacao(id)
      definirQtdCursosAfetados(dados?.cursoId?.length ?? 0)
    } catch {
      definirQtdCursosAfetados(null)
    }
  }

  const confirmarAcaoPendente = async () => {
    if (!acaoPendente) return

    const { tipo, id } = acaoPendente

    setCarregandoAcaoPendente(true)
    try {
      if (tipo === 'inativar') {
        await Api.InativarPrecificacao(id)
        toast('Precificação inativada com sucesso', { type: 'success' })
      } else {
        await Api.ExcluirPrecificacao(id)
        toast('Precificação excluída com sucesso', { type: 'success' })
      }
      tabelaRef.current?.CarregarDados()
    } catch {
      toast('Ops! Aconteceu um erro. Tente novamente.', { type: 'error' })
    } finally {
      setCarregandoAcaoPendente(false)
      modalConfirmacaoRef.current?.fechar()
      setAcaoPendente(null)
      definirQtdCursosAfetados(null)
    }
  }

  const obterDados = async (
    numeroPaginas: number,
    qtdRegistros: number,
    chaveOrdenacao?: string,
    ordem?: TipoOrdenacao
  ): Promise<TabelaResposta | null> => {
    if (paginas !== null) {
      numeroPaginas = paginas
    }

    const ativo =
      dadosPesquisa?.ativo === 'true'
        ? true
        : dadosPesquisa?.ativo === 'false'
        ? false
        : undefined

    const dados = await Api.ObterDetalhesPrecificacao(
      dadosPesquisa?.nome,
      dadosPesquisa?.codigo,
      dadosPesquisa?.categoria,
      numeroPaginas,
      qtdRegistros,
      chaveOrdenacao,
      ordem,
      ativo,
      cursoSelecionado
    )

    if (!dados) return null

    setPaginas(null)

    return {
      Dados: dados.registros,
      Paginacao: {
        Pagina: dados?.opcoesPaginacao?.pagina || 1,
        TotalRegistros: dados?.opcoesPaginacao?.totalRegistros || 1
      }
    } as TabelaResposta
  }

  return (
    <>
      <Cabecalho>
        <Breadcrumb
          titulo="Pesquisa de Precificações"
          atalhos={[
            {
              texto: 'Financeiro'
            },
            {
              texto: 'Precificação'
            }
          ]}
        />
        <Botao
          type="button"
          texto="Incluir"
          tema="Secundario"
          onClick={() => history.push(RotasPrecificacao.Cadastro)}
        />
      </Cabecalho>
      <Filtro>
        <Form acaoSucesso={acaoPesquisa}>
          <Container>
            <Campo>
              <InputUnform
                id="codigo"
                name="codigo"
                label="Código da precificação"
                type="text"
                maxLength={50}
              />
            </Campo>
            <Campo>
              <InputUnform
                id="nome"
                name="nome"
                label="Nome da precificação"
                type="text"
                maxLength={200}
              />
            </Campo>
            <Campo>
              <SelectUnform
                id="categoria"
                name="categoria"
                label="Categoria"
                opcoes={categoriasPrecificacao}
                iconeFechar
              />
            </Campo>
          </Container>
          <Container>
            <Campo largura="calc(50% - 10px)" alinhamento="flex-start">
              <SelectUnform
                id="ativo"
                name="ativo"
                label="Status da base"
                opcoes={opcoesStatus}
                iconeFechar
              />
            </Campo>
            <Campo largura="calc(50% - 10px)" alinhamento="flex-start">
              <SelectAsyncUnform
                id="curso"
                name="curso"
                label="Curso"
                buscarPorTexto={RequisitarListaCursosPorTextoFiltro}
                buscarPorId={RequisitarListaCursosPorTextoFiltro}
                valorAlterado={cursoAlterado}
              />
            </Campo>
          </Container>
          <Botao
            type="submit"
            id="btn-aplicar-filtro"
            texto="Aplicar filtro"
            tema="Link"
          />
        </Form>
      </Filtro>
      <Dados>
        <Tabela
          ref={tabelaRef}
          Colunas={[
            {
              Chave: 'nome',
              Texto: 'Nome',
              ComOrdenacao: true
            },
            {
              Chave: 'codigo',
              Texto: 'Cód.',
              ComOrdenacao: true
            },
            {
              Chave: 'categoria',
              Texto: 'Categoria',
              ComOrdenacao: true,
              StyleColuna: { whiteSpace: 'nowrap' },
              Renderizar: (linha: TabelaDados) => {
                if (!linha.categoria) return <></>

                return <>{PrecificacaoCategoriaPorExtenso[linha.categoria]}</>
              }
            },
            {
              Chave: 'dataInicio',
              Texto: 'Data de Início',
              ComOrdenacao: true,
              StyleColuna: { width: '110px', whiteSpace: 'nowrap' },
              Renderizar: (linha: TabelaDados) => (
                <>
                  {linha.dataInicio !== null
                    ? FuncoesDataHora.dataFormatadaComBarras(
                        new Date(linha.dataInicio)
                      )
                    : ''}
                </>
              )
            },
            {
              Chave: 'dataFim',
              Texto: 'Data de Fim',
              ComOrdenacao: true,
              StyleColuna: { width: '110px', whiteSpace: 'nowrap' },
              Renderizar: (linha: TabelaDados) => (
                <>
                  {linha.dataFim !== null
                    ? FuncoesDataHora.dataFormatadaComBarras(
                        new Date(linha.dataFim)
                      )
                    : ''}
                </>
              )
            },
            {
              Chave: 'ativo',
              Texto: 'Status',
              Alinhamento: 'center',
              StyleColuna: { width: '90px', whiteSpace: 'nowrap' },
              Renderizar: (linha: TabelaDados) => (
                <>{linha.ativo ? 'Ativa' : 'Inativa'}</>
              )
            },
            {
              Chave: 'acoes',
              Alinhamento: 'center',
              Renderizar: (linha: TabelaDados) => (
                <ContainerAcoes>
                  <button
                    type="button"
                    data-tip="Editar"
                    data-for={`${linha.id}-tooltip-editar`}
                    onClick={() => {
                      history.push(`${RotasPrecificacao.Edicao}/${linha.id}`)
                    }}
                  >
                    {IconeEditar}
                  </button>
                  <Tooltip
                    id={`${linha.id}-tooltip-editar`}
                    place="bottom"
                    offset={{ top: 10 }}
                  />
                  {linha.ativo && (
                    <>
                      <button
                        type="button"
                        data-tip="Inativar"
                        data-for={`${linha.id}-tooltip-inativar`}
                        onClick={() => abrirConfirmacao('inativar', linha.id)}
                      >
                        {React.cloneElement(IconeBloqueado, {
                          children: React.Children.map(
                            IconeBloqueado.props.children,
                            (child: JSX.Element) =>
                              React.cloneElement(child, { fill: 'inherit' })
                          )
                        })}
                      </button>
                      <Tooltip
                        id={`${linha.id}-tooltip-inativar`}
                        place="bottom"
                        offset={{ top: 10 }}
                      />
                    </>
                  )}
                  <button
                    type="button"
                    data-tip="Excluir"
                    data-for={`${linha.id}-tooltip-excluir`}
                    onClick={() => abrirConfirmacao('excluir', linha.id)}
                  >
                    {IconeLixeira}
                  </button>
                  <Tooltip
                    id={`${linha.id}-tooltip-excluir`}
                    place="bottom"
                    offset={{ top: 10 }}
                  />
                </ContainerAcoes>
              )
            }
          ]}
          ObterDados={obterDados}
          ComPaginacao
        />
      </Dados>
      <Modal
        ref={modalConfirmacaoRef}
        backdrop
        id="modal-confirmacao-acao-precificacao"
        icone={IconeAtencao}
        titulo={
          acaoPendente?.tipo === 'excluir'
            ? 'Deseja excluir esta precificação?'
            : 'Deseja inativar esta precificação?'
        }
        acaoPrimaria={{
          titulo: 'Confirmar',
          tipo: 'button',
          acao: confirmarAcaoPendente,
          carregando: carregandoAcaoPendente,
          desabilitado: carregandoAcaoPendente
        }}
        acaoSecundario={{
          titulo: 'Cancelar',
          tipo: 'button',
          desabilitado: carregandoAcaoPendente,
          acao: () => {
            modalConfirmacaoRef.current?.fechar()
            setAcaoPendente(null)
            definirQtdCursosAfetados(null)
          }
        }}
      >
        <TextoConfirmacaoModal>
          <p>
            {acaoPendente?.tipo === 'excluir'
              ? 'Essa ação não poderá ser desfeita.'
              : 'Os planos de pagamento vinculados a esta precificação deixarão de ser ofertados.'}
          </p>
          {qtdCursosAfetados > 0 && (
            <CalloutAlertaModal>
              Esta precificação é a base ativa de {qtdCursosAfetados} curso
              {qtdCursosAfetados > 1 ? 's' : ''}, que ficará
              {qtdCursosAfetados > 1 ? 'ão' : ''} sem precificação base após
              esta ação. Nenhuma outra precificação será reativada
              automaticamente.
            </CalloutAlertaModal>
          )}
        </TextoConfirmacaoModal>
      </Modal>
    </>
  )
}

export { default as PaginaCadastroPrecificacao } from './cadastro'
